import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { DateTime } from 'luxon';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { mapStackTrace } from 'sourcemapped-stacktrace';
import { v4 } from 'uuid';
import { LoggerService } from '../../../common-api/tool-logger-api';

import { BusinessErrorEnum } from '../../../core/enums';
import { AdvanceError } from '../../../core/error';
import { Log } from '../../../core/models';
import {
  HintDialogOption,
  HintDialogService,
  RouteStateService,
} from '../../../core/services';
import { isNullOrEmpty } from '../../../core/utils';

import { getDescription } from '../../../common/decorators/description.decorator';
import { UserComposerService } from '../../../core/services';
import { ToastService } from '../../ui/toast/toast.service';
import { AppError } from './models/app-error';

/**
 * App error type enum
 *
 * @export
 * @enum {number}
 */
export enum ErrorType {
  ApiError = 0,
  BusinessError,
  UnknownError,
  UnAuthorizedError,
}

/**
 * Event data for ErrorService to emit.
 *
 * @export
 * @interface ErrorEvent
 */
export interface ErrorEvent {
  type: ErrorType;
  error: Error;
}
declare type HitInfo = { hitCount: number; firstHitTime: Date };
const MAX_HIT_COUNT = 50;
const AVALAIBLE_SEND_DURATION = 5 * 60 * 1000;
/**
 * Error Handling Service
 *
 * @export
 * @class ErrorService
 */
@Injectable()
export class ErrorService {
  private logHitMap = new Map<string, HitInfo>();

  constructor(
    private logger: NGXLogger,
    private loggerService: LoggerService,
    private zone: NgZone,
    private toastService: ToastService,
    private hintDialogService: HintDialogService,
    private translateService: TranslateService,
    private routeStateService: RouteStateService,
    private userComposerService: UserComposerService
  ) {}
  private _errorSubject$ = new Subject<ErrorEvent>();
  /**
   * dialog 顯示錯誤資訊
   *
   * @param {HttpErrorResponse} err
   * @memberof ErrorService
   */
  showBasicError(err: HttpErrorResponse): void {
    this.hintDialogService.showErrorHint(
      JSON.stringify({
        statusCode: err.status,
        message: err.message,
        error: err.error,
      })
    );
  }

  /**
   * 處理無權限錯誤
   *
   * @param {*} error
   * @memberof ErrorService
   */
  handleUnAuthorizedError(error: AppError): void {
    const logErr = new AdvanceError(
      error,
      'Frontend error (Unauthorized)',
      undefined,
      window.location.href
    );
    this.writeLog('Frontend error (Unauthorized)', logErr);
    const event: ErrorEvent = {
      type: ErrorType.UnAuthorizedError,
      error,
    };
    this._errorSubject$.next(event);
    const msg = this.translateService.instant('Unauthorized');
    this.toastService.error(msg);

    this.routeStateService.navigateTo('/no-permission');
  }

  /**
   * 處理非預期的錯誤
   *
   * @param {*} error
   * @memberof ErrorService
   */
  handleUnknownError(error: string | Error | undefined): void {
    const logErr = new AdvanceError(
      error,
      'Frontend error (Unknown)',
      undefined,
      window.location.href,
    );
    this.writeLog('Frontend error (Unknown)', logErr);

    const msg = this.translateService.instant('Unknown Error');
    this.toastService.error(msg);
  }

  /**
   * 處理 api 的錯誤
   *
   * @param {*} error
   * @param {string} clientTraceId
   * @memberof ErrorService
   */
  handleApiError(
    error: string | Error | undefined,
    clientTraceId: string
  ): void {
    const logErr = new AdvanceError(
      error,
      'Api error',
      undefined,
      this.routeStateService.currentUrl
    );
    this.writeLog('Api error', logErr);

    const msg = `Client-TraceId: ${clientTraceId}`;
    this.toastService.apiError(msg);
  }

  /**
   * 處理商業邏輯錯誤
   *
   * @param {string} errCode
   * @param {*} errorBody
   * @return {*}  {boolean}
   * @memberof ErrorService
   */
  async handleBusinessError(errCode: string, errorBody: any): Promise<boolean> {
    switch (errCode) {
      case 'FORMAT.RequestError':
        // Format errors
        const msgs = Object.values<any>(errorBody.errors)
          .reduce((sum, item) => sum.concat(item), [])
          .distinct();
        const hintOption: HintDialogOption = {
          headerIcon: 'pi pi-warning',
          message: msgs.length === 1 ? { category: msgs[0] } : {},
          messages:
            msgs.length > 1
              ? msgs.map((x: any) => ({ category: x }))
              : undefined,
          header: 'System Notification',
          hintType: msgs.length > 1 ? 'list' : 'message',
          cancelText: 'OK',
        };
        this.hintDialogService.showHint(hintOption);
        return true;
      case 'FORMAT.CustomError':
        return false;
      case 'TRNS.BpmAdapterErr':
        this.logger.error('TRNS.BpmAdapterErr', errorBody);
        try {
          if (errCode.includes('TRNS.BpmAdapterErr')) {
            const prop = Object.getOwnPropertyNames(BusinessErrorEnum).find(
              (x) =>
                BusinessErrorEnum[x as keyof typeof BusinessErrorEnum] ===
                'TRNS.BpmErr'
            );

            let bpmMsg = this.translateService.instant(
              getDescription(BusinessErrorEnum, prop!)
            ); // Defined errors
            this.logger.error('TRNS.BpmAdapterErr');
            const msg =
              JSON.parse(errorBody.split(']')?.[1])?.msg ??
              JSON.parse(errorBody.split(']')?.[1]);
            this.hintDialogService.showErrorHint(`${msg}`);
          }
        } catch (error) {}
        return true;
      default:
        const prop = Object.getOwnPropertyNames(BusinessErrorEnum).find(
          (x) =>
            BusinessErrorEnum[x as keyof typeof BusinessErrorEnum] === errCode
        );

        if (isNullOrEmpty(prop)) {
          return false;
        }
        let msg = this.translateService.instant(
          getDescription(BusinessErrorEnum, prop!)
        ); // Defined errors

        if (errCode === BusinessErrorEnum.VcifLocked) {
          const errMsg = errorBody.split(']');
          if (errMsg.length > 1) {
            const vcifMsg = errMsg[1].split(',');
            try {
              let result = await firstValueFrom(
                this.userComposerService.getUserInfo(vcifMsg[0])
              );
              const userName = result?.userName ?? '';
              if (vcifMsg.length > 1) {
                msg = msg
                  .replace(
                    '{lockedByUser}',
                    ` ${vcifMsg[0]} ` + ` ${userName} `
                  )
                  .replace('{lockedBySystem}', vcifMsg[1]);
              }
            } catch (error: any) {
              this.logger.error(error);
            }
          }
        }

        this.hintDialogService.showErrorHint(msg);
        return true;
    }
  }

  /**
   * 寫入 log
   *
   * @private
   * @param {string} title
   * @param {AdvanceError} logErr
   * @memberof ErrorService
   */
  private writeLog(title: string, logErr: AdvanceError): void {
    this.zone.run(() => {
      this.logger.error(logErr);
      // 對應 source map 程式路徑
      mapStackTrace(
        logErr.stack,
        (mappedStack: any[]) => {
          logErr.stack = mappedStack.join('\n');

          const clientTraceId = v4();
          const log = new Log(
            title,
            logErr.toString(),
            clientTraceId,
          );
          this.logger.debug('Send log: ', log);

          if ([0].includes(globalThis.sigv.appContext.envConfig.logLevel)) {
            this.writeApiLog(log);
          }
        },
        { cacheGlobally: true }
      );
    });
  }

  /**
   * 呼叫 log api
   *
   * @private
   * @param {Log} log
   * @return {*}  {void}
   * @memberof ErrorService
   */
  private writeApiLog(log: Log): void {
    const now = DateTime.now().toJSDate();
    const logKey = log.body;

    // 清掉超過要檢查的時間區間的 log
    Array.from(this.logHitMap.entries()).forEach((entry) => {
      const key = _.first(entry) as string;
      const value = _.last(entry) as HitInfo;
      if (
        now.getTime() - value.firstHitTime.getTime() >
        AVALAIBLE_SEND_DURATION
      ) {
        this.logHitMap.delete(key);
      }
    });

    // 檢查是否可發送 log
    if (this.logHitMap.has(log.body)) {
      const hitInfo = this.logHitMap.get(logKey) as HitInfo;
      hitInfo.hitCount++;
      // 檢查是否超過重複發送的最大數量
      const exceedMaxHitCount = hitInfo.hitCount > MAX_HIT_COUNT;
      if (exceedMaxHitCount) {
        this.logger.debug('This log hits the max send count.', log);
        return;
      }
    } else {
      this.logHitMap.set(logKey, { hitCount: 1, firstHitTime: now });
    }

    // 執行呼叫 log api
    this.loggerService.loggerErrorPost(log).subscribe({
      next: (result) => {},
      error: (err) => {
        this.logger.error(err);
      },
    });
  }
  /**
   * Get app error subject as observable type.
   *
   * @return {*}  {Observable<ErrorEvent>}
   * @memberof ErrorService
   */
  getErrorSubject(): Observable<ErrorEvent> {
    return this._errorSubject$.asObservable();
  }
}
